import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  ArrowRight, 
  Star, 
  ChevronRight,
  Zap,
  Award,
  TrendingUp
} from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { format } from 'date-fns';

// Lazy load non-critical components
//const TrendingSection = lazy(() => import('../components/TrendingSection'));
//const NewsletterSignup = lazy(() => import('../components/NewsletterSignup'));

// --- Optimized Image Component ---
const OptimizedImage = ({ src, alt, className, priority = false, srcSet = null }) => {
  const [imageSrc, setImageSrc] = useState(priority ? src : null);
  const [imageRef, setImageRef] = useState(null);

  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(entry.target.dataset.src);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '50px' }
    );

    if (imageRef) observer.observe(imageRef);
    return () => observer.disconnect();
  }, [imageRef, priority]);

  return (
    <img
      ref={setImageRef}
      data-src={src}
      src={imageSrc || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3C/svg%3E'}
      srcSet={srcSet}
      alt={alt}
      className={`${className} ${!imageSrc ? 'blur-sm' : ''} transition-all duration-300`}
      loading={priority ? 'eager' : 'lazy'}
    />
  );
};

// --- Sub-Components ---
const SectionHeader = ({ title, link, linkText, subtitle }) => (
  <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-8">
    <div>
      <h2 className="text-4xl font-black text-gray-900 mb-2">{title}</h2>
      {subtitle && <p className="text-gray-600 text-sm md:text-base">{subtitle}</p>}
    </div>
    {link && (
      <Link 
        to={link} 
        className="group inline-flex items-center text-sm font-bold text-red-600 hover:text-red-700 transition-all hover:gap-2"
      >
        {linkText || 'View All'}
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Link>
    )}
  </div>
);

// --- Skeleton Loaders ---
const ArticleSkeleton = () => (
  <div className="animate-pulse flex gap-6 bg-white p-6 rounded-2xl border border-gray-100">
    <div className="w-1/3 h-48 bg-gray-200 rounded-xl"></div>
    <div className="flex-1 space-y-4">
      <div className="h-4 w-24 bg-gray-200 rounded"></div>
      <div className="h-6 w-3/4 bg-gray-200 rounded"></div>
      <div className="h-20 w-full bg-gray-200 rounded"></div>
    </div>
  </div>
);

const ReviewCardSkeleton = () => (
  <div className="animate-pulse bg-gray-200 rounded-2xl h-80"></div>
);

// --- Optimized Article Card ---
const ArticleCard = ({ article, idx, priority = false }) => (
  <div className="group relative overflow-hidden rounded-2xl bg-white border border-gray-100 hover:border-red-200 shadow-sm hover:shadow-2xl transition-all duration-300 flex flex-col md:flex-row h-full">
    <div className="w-full md:w-2/5 h-56 md:h-64 relative overflow-hidden bg-gray-100">
      <OptimizedImage
        src={article.image_url}
        alt={article.title}
        priority={priority}
        srcSet={`${article.image_url}?w=400&h=300&fit=crop 400w, ${article.image_url}?w=800&h=600&fit=crop 800w`}
        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
      />
      {idx === 0 && (
        <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-black">
          <Zap className="w-3 h-3" /> NEW
        </div>
      )}
    </div>
    
    <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
      <div>
        <div className="flex items-center gap-2 text-xs text-red-600 font-black uppercase tracking-widest mb-3">
          <Calendar className="w-4 h-4 flex-shrink-0" />
          <time dateTime={article.created_at}>
            {format(new Date(article.created_at), 'MMM d, yyyy')}
          </time>
        </div>
        <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-3 leading-tight group-hover:text-red-600 transition-colors">
          <Link to={`/read/${article.id}`}>
            {article.title}
          </Link>
        </h3>
        <p className="text-gray-600 line-clamp-2 text-base leading-relaxed">
          {article.excerpt}
        </p>
      </div>
      <Link 
        to={`/read/${article.id}`} 
        className="inline-flex items-center gap-2 text-red-600 font-black text-sm uppercase tracking-wide mt-6 hover:gap-3 transition-all"
      >
        Read Full Story <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

// --- Optimized Review Card ---
const ReviewCard = ({ review, priority = false }) => (
  <Link to={`/read/${review.id}`} className="group h-full">
    <div className="h-full bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col">
      <div className="relative h-56 overflow-hidden bg-gray-100">
        <OptimizedImage
          src={review.image_url}
          alt={review.car_model}
          priority={priority}
          srcSet={`${review.image_url}?w=300&h=224&fit=crop 300w, ${review.image_url}?w=600&h=448&fit=crop 600w`}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4 bg-white shadow-lg px-3 py-2 rounded-xl flex items-center gap-2 backdrop-blur-sm bg-white/95">
          <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
          <span className="font-black text-gray-900">{review.rating}/10</span>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-lg font-black text-gray-900 mb-3 group-hover:text-red-600 transition-colors leading-tight">
          {review.car_model}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
          {review.excerpt}
        </p>
        <div className="flex items-center gap-2 text-red-600 font-black text-xs uppercase tracking-wide group-hover:gap-3 transition-all">
          Read Review <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  </Link>
);

// --- Skeleton Wrapper ---
const SkeletonLoader = ({ count, component: Component }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <Component key={i} />
    ))}
  </>
);

// --- Main Component ---
const Home = () => {
  const [heroArticle, setHeroArticle] = useState(null);
  const [latestNews, setLatestNews] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { supabase } = useSupabase();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch in parallel for better performance
        const [articleRes, reviewRes] = await Promise.all([
          supabase
            .from('articles')
            .select('id, title, excerpt, image_url, created_at, published')
            .eq('published', true)
            .order('created_at', { ascending: false })
            .limit(8),
          supabase
            .from('reviews')
            .select('id, car_model, excerpt, image_url, rating, created_at, published')
            .eq('published', true)
            .order('created_at', { ascending: false })
            .limit(4)
        ]);

        if (articleRes.data && articleRes.data.length > 0) {
          setHeroArticle(articleRes.data[0]);
          setLatestNews(articleRes.data.slice(1, 5));
        }
        
        setReviews(reviewRes.data || []);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [supabase]);

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'MotorsBay.co.uk - Car News & Reviews',
    description: 'Expert car reviews, news, and comparisons for UK automotive enthusiasts',
    url: 'https://motorsbay.co.uk',
    mainEntity: {
      '@type': 'NewsMediaOrganization',
      name: 'motorsbay',
      url: 'https://motorsbay.co.uk',
      logo: 'https://motorsbay.co.uk/logo.png'
    }
  };

  useEffect(() => {
    // Add structured data to head
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => document.head.removeChild(script);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <SkeletonLoader count={4} component={ArticleSkeleton} />
          </div>
          <div className="space-y-6">
            <SkeletonLoader count={2} component={ReviewCardSkeleton} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen font-sans text-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* LEFT COLUMN: LATEST NEWS */}
          <div className="lg:col-span-8">
            <SectionHeader 
              title="Latest News" 
              subtitle="Stay updated with breaking automotive stories"
              link="/news" 
            />
            
            <div className="space-y-6">
              {latestNews.map((article, idx) => (
                <ArticleCard key={article.id} article={article} idx={idx} priority={idx === 0} />
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN: SIDEBAR 
          <div className="lg:col-span-4 space-y-8">
            <Suspense fallback={<div className="h-80 bg-white rounded-2xl animate-pulse" />}>
              <TrendingSection />
            </Suspense>
          </div>

          */}
        </div>
      </div>

      {/* REVIEWS SECTION */}
      <div className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader 
            title="Car Reviews" 
            subtitle="Expert opinions on the latest vehicles"
            link="/reviews" 
            linkText="See All Reviews" 
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((review, idx) => (
              <ReviewCard key={review.id} review={review} priority={idx < 2} />
            ))}
          </div>
        </div>
      </div>

      {/* BOTTOM CTA */}
      <div className="relative py-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-700 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 bg-red-600/20 text-red-400 px-4 py-2 rounded-full text-sm font-bold">
            <Award className="w-4 h-4" />
            Explore Our Collection
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
            Ready to find your <span className="text-red-500">dream car?</span>
          </h1>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Dive into thousands of expert reviews, detailed comparisons, and the latest automotive news all in one place.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/reviews" 
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-black text-base transition-all transform hover:scale-105 shadow-lg shadow-red-900/30 inline-flex items-center justify-center gap-2"
            >
              <Star className="w-5 h-5" /> Read Reviews
            </Link>
            <Link 
              to="/news" 
              className="bg-white/10 hover:bg-white/20 border-2 border-white/30 text-white px-8 py-4 rounded-xl font-black text-base transition-all inline-flex items-center justify-center gap-2"
            >
              <Zap className="w-5 h-5" /> Browse News
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Home;